import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { can, type DriverDetail, type DriverStatusRow } from '@torapon/shared';
import { api, HttpError } from '../lib/api';
import { sendInvite } from '../lib/invite';
import { fmtDateTime, fmtTime } from '../lib/format';
import { useDirectory, useNames } from '../lib/directory';
import { rollCallDetails } from '../lib/fs';
import { DeliveryActions, DeliveryStatusPill, route } from '../components/DeliveryActions';
import { Dialog, useToast } from '../components/ui';
import { useMe } from '../lib/me';
import { STATE, ago, cautions, hm } from '../lib/driverState';
import { RetireDialog } from './StaffPage';
import { COLORS, TYPE } from './DriversPage';
import { useStepUp } from '../components/StepUp';
import { clearInvite, setRetireAt, updatePerson } from '../lib/fs';
import { DriverCreate, lockKeys, type Driver, type Vehicle } from '@torapon/shared';
import type { RollCallDetailDoc } from '@torapon/shared';
import { LockBanner, useEditLock } from '../lib/locks';
import { DrivingPanel } from '../components/DrivingPanel';

const RC = { waiting: '確認待ち', confirmed: '確認済み', remeasure: '再測定を依頼', rejected: '乗務させない', not_done: '未実施' } as const;

/** ドライバー詳細（仕様書「ドライバー詳細」）。運転時間・休憩・停車はドライバーアプリをつないでから出す */
export function DriverDetailPage() {
  const { id } = useParams();
  const [d, setD] = useState<DriverDetail | null>(null);
  const [err, setErr] = useState('');
  const [details, setDetails] = useState<Map<string, RollCallDetailDoc>>(new Map());
  const dir = useDirectory();
  const names = useNames();
  useEffect(() => { if (d) rollCallDetails(d.rollCalls.map((r) => r.id)).then(setDetails); }, [d]);
  const me = useMe();
  const toast = useToast();
  const [st, setSt] = useState<DriverStatusRow | null>(null);
  const [ending, setEnding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [retiring, setRetiring] = useState(false);
  const { ensureRecent } = useStepUp();
  const reinstate = async () => {
    if (!d || !(await ensureRecent('ドライバーの再雇用'))) return;
    try { await api.post(`/v1/office/drivers/${d.driver.id}/reinstate`, {}, { ifMatch: d.driver.version }); await setRetireAt('drivers', d.driver.id, null); toast('有効に戻しました'); load(); }
    catch (e) { toast(e instanceof HttpError ? e.message : '戻せませんでした'); }
  };
  const [endErr, setEndErr] = useState('');
  const handleDelete = async (reqId: string) => {
    try { await api.post(`/v1/office/delete-requests/${reqId}/handle`, {}); toast('アカウント削除の申請を対応済みにしました'); await load(); }
    catch (e) { toast(e instanceof HttpError ? e.message : '記録できませんでした'); }
  };
  // 直したあとは、読み直しが済んでから閉じる（古い版番号のまま次の操作をすると 409 になるため）
  const load = () => Promise.all([
    api.get<DriverDetail>(`/v1/office/drivers/${id}`).then(setD).catch((e) => setErr(e instanceof HttpError ? e.message : '読み込めませんでした')),
    api.get<{ drivers: DriverStatusRow[] }>('/v1/office/drivers/status').then((r) => setSt(r.drivers.find((x) => x.driverId === id) ?? null)).catch(() => {}),
  ]);
  useEffect(() => { void load(); }, [id]);
  // 勤務終了の押し忘れ（仕様書 E5）：乗務後の点呼は「未実施」で残る
  const forceEnd = async () => {
    setEndErr('');
    try { await api.post(`/v1/office/shifts/${st!.shiftId}/force-end`, {}); setEnding(false); toast('勤務を終了にしました。乗務後の点呼は「未実施」で記録しました'); load(); }
    catch (e) { setEndErr(e instanceof HttpError ? e.message : '終了できませんでした'); }
  };
  if (err) return <div className="page"><p className="err">{err}</p></div>;
  if (!d) return <div className="page"><p className="muted">読み込み中…</p></div>;
  const s = d.shift;
  const state = st ? STATE[st.state] : (['—', ''] as const);
  const name = dir.drivers.get(d.driver.id)?.name ?? '—';
  return (
    <div className="page" style={{ display: 'grid', gridTemplateColumns: '320px minmax(0,1fr)', gap: 16, alignItems: 'start' }}>
      <section className="panel" style={{ padding: 18, display: 'grid', gap: 10 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}><span style={{ width: 16, height: 16, borderRadius: 5, background: d.driver.color }} /><b style={{ fontSize: 20 }}>{dir.drivers.get(d.driver.id)?.name ?? '—'}</b></div>
        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}><span className={`pill ${state[1]}`}>{state[0]}</span>{st && cautions(st).map((x) => <span key={x} className="pill warn">{x}</span>)}</span>
        {d.deleteRequest && (
          <div role="status" style={{ border: '1px solid var(--alert)', borderRadius: 10, padding: 10, display: 'grid', gap: 6 }}>
            <b className="err">アカウント削除の申請（{fmtDateTime(d.deleteRequest.requestedAt)}）</b>
            <small>本人に確かめてから「退職の手続き」をし、そのあと「対応済みにする」を押してください。点呼や運転日報などの記録は法令の期間残ります。</small>
            {me && can(me.role, 'register') && <button className="btn sm" style={{ justifySelf: 'start' }} onClick={() => void handleDelete(d.deleteRequest!.id)}>対応済みにする</button>}
          </div>
        )}
        <dl style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: '6px 10px', margin: 0 }}>
          <dt className="muted">電話</dt><dd style={{ margin: 0 }} className="num">{dir.drivers.get(d.driver.id)?.phone ?? '—'}</dd>
          <dt className="muted">アカウント</dt><dd style={{ margin: 0, overflowWrap: 'anywhere' }}>{dir.drivers.get(d.driver.id)?.email ?? '—'}</dd>
          <dt className="muted">車両</dt><dd style={{ margin: 0 }}>{s?.vehicle ?? '—'}</dd>
          <dt className="muted">始業</dt><dd style={{ margin: 0 }} className="num">{s ? fmtDateTime(s.workStartAt) : '—'}</dd>
          <dt className="muted">終業</dt><dd style={{ margin: 0 }} className="num">{s?.workEndAt ? fmtDateTime(s.workEndAt) : '—'}</dd>
          <dt className="muted">連続運転</dt><dd style={{ margin: 0 }} className="num">{hm(st?.continuousDriveMin ?? 0)}</dd>
          <dt className="muted">拘束時間</dt><dd style={{ margin: 0 }} className="num">{hm(st?.restraintMin ?? null)}</dd>
          <dt className="muted">運転</dt><dd style={{ margin: 0 }} className="num">{s ? hm(s.drivingMin) : '—'}</dd>
          <dt className="muted">休憩</dt><dd style={{ margin: 0 }} className="num">{s ? hm(s.breakMin) : '—'}</dd>
          <dt className="muted">理由不明の停車</dt><dd style={{ margin: 0 }} className={`num${s && s.unknownStopMin > 0 ? ' err' : ''}`}>{s ? hm(s.unknownStopMin) : '—'}</dd>
          <dt className="muted">本日の走行</dt><dd style={{ margin: 0 }} className="num">{st?.shiftId ? `${st.distanceKm} km` : '—'}</dd>
          <dt className="muted">端末</dt><dd style={{ margin: 0 }}>{st?.device ? `${st.device.battery != null ? `電池 ${st.device.battery}%・` : ''}${ago(st.device.lastSeenAt)}` : '—'}</dd>
        </dl>
        {st?.shiftId && me && can(me.role, 'confirmRollCall') && <button className="btn danger" style={{ justifySelf: 'start' }} onClick={() => { setEnding(true); setEndErr(''); }}>勤務を終了させる</button>}
        {me && can(me.role, 'register') && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {d.driver.status !== 'retired' ? <>
              <button className="btn sm" onClick={() => setEditing(true)}>情報を直す</button>
              <button className="btn sm danger" onClick={() => setRetiring(true)}>退職の手続き</button>
            </> : <><span className="pill">退職{d.driver.retiredAt ? `（${fmtDateTime(d.driver.retiredAt)} から）` : ''}</span><button className="btn sm" onClick={reinstate}>再雇用</button></>}
          </div>
        )}
        <Link to="/drivers" className="btn sm" style={{ justifySelf: 'start', textDecoration: 'none' }}>← ドライバー一覧</Link>
      </section>
      <div style={{ display: 'grid', gap: 16 }}>
        <DeviceConsent detail={d} onChanged={load} />
        <DaysOff driverId={d.driver.id} />
        {me && can(me.role, 'viewRecords') && <DrivingPanel driverId={d.driver.id} name={name} />}
        <section className="panel">
          <div style={{ padding: '14px 16px 0' }}><b>点呼（この 24 時間）</b></div>
          <table><thead><tr><th>時刻</th><th>区分</th><th>測定値</th><th>確認者</th><th>状態</th></tr></thead>
            <tbody>{d.rollCalls.length === 0 ? <tr><td colSpan={5} className="muted">まだありません</td></tr> : d.rollCalls.map((r) => (
              <tr key={r.id}><td className="num">{fmtTime(r.at)}</td><td>{r.kind === 'before' ? '乗務前' : '乗務後'}</td><td className="num">{details.get(r.id)?.alcohol.toFixed(2) ?? '—'}</td><td>{names.staff(r.checkedBy)}</td><td>{RC[r.status]}</td></tr>
            ))}</tbody></table>
        </section>
        <section className="panel">
          <div style={{ padding: '14px 16px 0' }}><b>今日の配送</b></div>
          <table><thead><tr><th>配送番号</th><th>積む → 届ける</th><th>必着</th><th>状態</th><th>写真</th><th>問題報告</th><th></th></tr></thead>
            <tbody>{d.deliveries.length === 0 ? <tr><td colSpan={7} className="muted">まだありません</td></tr> : d.deliveries.map((x) => { const ex = d.deliveryExtras[x.deliveryNo]; return (
              <tr key={x.deliveryNo}><td className="num"><b>{x.deliveryNo}</b></td><td>{route(x, names)}</td><td className="num">{x.dueAt ?? '—'}</td>
                <td><DeliveryStatusPill status={x.status} /></td>
                <td className="num">{ex?.photos ? `${ex.photos} 枚` : <span className="muted">—</span>}</td>
                <td>{!ex?.issues ? <span className="muted">なし</span> : <Link to="/issues" className={ex.openIssues ? 'err' : ''}>{ex.openIssues ? `未対応 ${ex.openIssues} 件` : `${ex.issues} 件（対応済み）`}</Link>}</td>
                <td><DeliveryActions row={x} onChanged={load} /></td></tr>); })}</tbody></table>
        </section>
      </div>
      {editing && <EditDriver driver={d.driver} onClose={() => setEditing(false)} onDone={async () => { await load(); setEditing(false); }} />}
      {retiring && <RetireDialog kind="drivers" id={d.driver.id} version={d.driver.version} name={name} onClose={() => setRetiring(false)} onDone={async () => { await load(); setRetiring(false); }} />}
      {ending && (
        <Dialog title={`${name}さんの勤務を終了させますか？`} onClose={() => setEnding(false)}>
          <p style={{ margin: 0 }}>勤務終了の押し忘れのときに使います。乗務後の点呼は「未実施」として記録に残ります。</p>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>終わっていない配送があるときは、先に付け替えか取り消しをしてください。</p>
          {endErr && <p className="err" role="alert">{endErr}</p>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button className="btn" onClick={() => setEnding(false)}>やめる</button><button className="btn danger" onClick={forceEnd}>終了させる</button></div>
        </Dialog>
      )}
    </div>
  );
}

/** ドライバーの情報を直す。アドレスを変えたら、ひもづけ（スマホ）を外す */
function EditDriver({ driver, onClose, onDone }: { driver: Driver; onClose: () => void; onDone: () => void }) {
  const me = useMe();
  const toast = useToast();
  const dir = useDirectory();
  const { ensureRecent } = useStepUp();
  const doc = dir.drivers.get(driver.id);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [f, setF] = useState({ name: doc?.name ?? '', phone: doc?.phone ?? '', email: doc?.email ?? '', accountType: driver.accountType, color: driver.color, vehicleId: driver.vehicleId ?? '' });
  const [err, setErr] = useState('');
  const lock = useEditLock(lockKeys.driverInfo(driver.id));
  useEffect(() => { api.get<{ vehicles: Vehicle[] }>('/v1/office/vehicles').then((r) => setVehicles(r.vehicles.filter((v) => v.status === 'active'))).catch(() => {}); }, []);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setErr('');
    const r = DriverCreate.safeParse({ ...f, phone: f.phone || undefined, vehicleId: f.vehicleId || undefined });
    if (!r.success) { setErr(r.error.issues[0]?.message ?? '入力を確かめてください'); return; }
    const emailChanged = r.data.email !== doc?.email;
    if (emailChanged && [...dir.drivers.entries()].some(([id, x]) => id !== driver.id && x.email === r.data.email)) { setErr('このメールアドレスはもう登録されています'); return; }
    if (!(await ensureRecent('ドライバーの情報の変更'))) return;
    try {
      await api.patch(`/v1/office/drivers/${driver.id}`, { accountType: r.data.accountType, color: r.data.color, vehicleId: r.data.vehicleId }, { ifMatch: driver.version });
      await updatePerson('drivers', driver.id, { name: r.data.name, phone: r.data.phone ?? null, email: r.data.email, ...(emailChanged ? { uid: null } : {}) });
      if (emailChanged && me) await clearInvite(me.officeId, driver.id);
      toast(emailChanged ? '直しました。アドレスを変えたので、スマホのひもづけを外しました（新しいアドレスでログインし直してもらいます）' : '直しました');
      onDone();
    } catch (er) { setErr(er instanceof HttpError ? er.message : '保存できませんでした'); }
  };
  return (
    <Dialog title="ドライバーの情報を直す" onClose={onClose}>
      <form onSubmit={submit} style={{ display: 'grid', gap: 10 }}>
        <div className="field"><label htmlFor="en">名前（必須）</label><input id="en" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div className="field"><label htmlFor="ep">電話番号</label><input id="ep" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
        <div className="field"><label htmlFor="em">ログインに使うアドレス（必須）</label><input id="em" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
        <div className="field"><label htmlFor="et">アカウントの種類</label><select id="et" value={f.accountType} onChange={(e) => setF({ ...f, accountType: e.target.value as Driver['accountType'] })}>{Object.entries(TYPE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
        <div className="field"><label htmlFor="ev">いつもの車両</label><select id="ev" value={f.vehicleId} onChange={(e) => setF({ ...f, vehicleId: e.target.value })}><option value="">決めない</option>{vehicles.map((v) => <option key={v.id} value={v.id}>{v.nickname}（{v.plateNumber}）</option>)}</select></div>
        <div className="field"><label>地図の色</label><div style={{ display: 'flex', gap: 6 }}>
          {COLORS.map((c) => <button type="button" key={c} aria-label={c} onClick={() => setF({ ...f, color: c })} style={{ width: 28, height: 28, borderRadius: 8, border: 0, background: c, outline: f.color === c ? '3px solid #000' : 'none', outlineOffset: 2, cursor: 'pointer' }} />)}
        </div></div>
        <LockBanner lock={lock} what="このドライバーの情報" />
        {err && <p className="err" role="alert">{err}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button type="button" className="btn" onClick={onClose}>やめる</button><button className="btn pri" disabled={!lock.mine}>直す</button></div>
      </form>
    </Dialog>
  );
}

const DEVICE_LABEL = { pending: '承認待ち', approved: '承認済み', revoked: '解除' } as const;

/** 端末の承認・解除と、同意の記録（仕様書「端末」「システムでの扱い」） */
function DeviceConsent({ detail, onChanged }: { detail: DriverDetail; onChanged: () => void }) {
  const me = useMe();
  const toast = useToast();
  const { ensureRecent } = useStepUp();
  const [date, setDate] = useState('');
  const [err, setErr] = useState('');
  const run = async (fn: () => Promise<unknown>, done: string) => {
    setErr('');
    try { await fn(); toast(done); onChanged(); } catch (e) { setErr(e instanceof HttpError ? e.message : '送れませんでした'); }
  };
  const act = async (id: string, what: 'approve' | 'revoke') => {
    if (await ensureRecent('端末の承認・解除')) await run(() => api.post(`/v1/office/devices/${id}/${what}`, {}), what === 'approve' ? '端末を承認しました' : '端末を解除しました');
  };
  const canApprove = !!me && can(me.role, 'approveDevice');
  return (
    <section className="panel" aria-label="端末と同意">
      <div style={{ padding: '14px 16px 0' }}><b>端末と同意</b></div>
      <table><thead><tr><th>端末</th><th>登録</th><th>状態</th>{canApprove && <th></th>}</tr></thead>
        <tbody>{detail.devices.length === 0 ? <tr><td colSpan={4} className="muted">まだありません</td></tr> : detail.devices.map((x) => (
          <tr key={x.id}><td className="num">{x.id.slice(-6)}</td><td className="num">{fmtDateTime(x.createdAt)}</td><td>{DEVICE_LABEL[x.status]}</td>
            {canApprove && <td>{x.status === 'pending' ? <button className="btn sm pri" onClick={() => void act(x.id, 'approve')}>承認する</button> : x.status === 'approved' ? <button className="btn sm danger" onClick={() => void act(x.id, 'revoke')}>解除する</button> : null}</td>}</tr>
        ))}</tbody></table>
      <div style={{ padding: 16, display: 'grid', gap: 8 }}>
        {me && can(me.role, 'register') && <button className="btn sm" style={{ justifySelf: 'start' }} onClick={() => void sendInvite('driver', detail.driver.id, toast)}>招待メールを送り直す</button>}
        <span>アプリの同意：{detail.consentAt ? fmtDateTime(detail.consentAt) : 'まだ'}</span>
        <span>紙の同意：{detail.paperConsentOn ?? 'まだ'}</span>
        {me && can(me.role, 'register') && <span style={{ display: 'flex', gap: 8, alignItems: 'end' }}>
          <span className="field" style={{ flex: 1 }}><label htmlFor="paper">紙の同意書に署名した日</label><input id="paper" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></span>
          <button className="btn" disabled={!date} onClick={() => void run(() => api.post(`/v1/office/drivers/${detail.driver.id}/paper-consent`, { signedOn: date }), '紙の同意を記録しました')}>記録する</button>
        </span>}
        {err && <p className="err" role="alert">{err}</p>}
      </div>
    </section>
  );
}

/** 休みの日（仕様書「月間スケジュール」：担当が休みの便は赤で示す） */
function DaysOff({ driverId }: { driverId: string }) {
  const toast = useToast();
  const [dates, setDates] = useState<string[] | null>(null);
  const [date, setDate] = useState('');
  const [err, setErr] = useState('');
  const load = () => api.get<{ dates: string[] }>(`/v1/office/drivers/${driverId}/days-off`).then((r) => setDates(r.dates)).catch(() => setDates([]));
  useEffect(() => { void load(); }, [driverId]);
  const add = async () => {
    setErr('');
    if (!date) { setErr('日付を選んでください'); return; }
    try { await api.post(`/v1/office/drivers/${driverId}/days-off`, { date }); setDate(''); toast('休みの日を足しました'); void load(); }
    catch (e) { setErr(e instanceof HttpError ? e.message : '足せませんでした'); }
  };
  const remove = async (x: string) => {
    try { await api.del(`/v1/office/drivers/${driverId}/days-off/${x}`); void load(); }
    catch (e) { toast(e instanceof HttpError ? e.message : '消せませんでした'); }
  };
  return (
    <section className="panel" aria-label="休みの日" style={{ padding: 16, display: 'grid', gap: 8 }}>
      <b>休みの日</b>
      <small className="muted">休みの日に定期便・予約の担当になっていると、予定で赤く出て警告します</small>
      <span style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {dates === null ? <span className="muted">読み込み中…</span> : dates.length === 0 ? <span className="muted">先の休みはありません</span>
          : dates.map((x) => <span key={x} className="pill">{x.slice(5).replace('-', '/')}<button className="btn sm" style={{ marginLeft: 4, padding: '0 6px' }} aria-label={`${x} の休みを消す`} onClick={() => void remove(x)}>✕</button></span>)}
      </span>
      <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input type="date" aria-label="休みにする日" value={date} onChange={(e) => setDate(e.target.value)} style={{ width: 170 }} />
        <button className="btn sm" onClick={() => void add()}>休みを足す</button>
        {err && <span className="err" role="alert">{err}</span>}
      </span>
    </section>
  );
}
